/**
 * Add one proposal structure after the project is already split.
 * The structure becomes its own Files group, or joins a group that already exists.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Layers, Loader2, X } from 'lucide-react';
import type { ProjectFile, ProjectFileStream } from '../../types';
import type { StructureRef } from '../../utils/proposalStructureRefs';
import type { AddStructureDestination } from '../../utils/addProjectStructure';
import { uploadStructureGroups } from '../../utils/projectFileStreamGroups';
import { headVersionForStream, versionsForStream } from '../../utils/projectFileStreamVersions';
import {
  detectTimberListGroups,
  suggestStructureNameFromTrack,
  timberListTracksForStructureSetup,
  timberListVersionOptionLabel,
  type StructureFromTimberListRow,
} from '../../utils/proposalStructuresFromTimberLists';
import { newStructureRowProblem } from '../../utils/addProjectStructure';
import { trackKindShort } from './projectUploadModalShared';
import './ProjectFiles.css';
import './SplitTracksModal.css';
import './CreateStructuresFromTimberListsModal.css';

export interface AddStructureModalProps {
  streams: ProjectFileStream[];
  files: ProjectFile[];
  structures: StructureRef[];
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (input: { row: StructureFromTimberListRow; destination: AddStructureDestination }) => void;
  fetchText?: (url: string) => Promise<string>;
}

const ROW_KEY = 'add';
const NONE_VALUE = '';

const emptyRow = (): StructureFromTimberListRow => ({
  key: ROW_KEY,
  name: '',
  existingStructureId: null,
  timberListStreamId: null,
  timberListFileId: null,
});

const defaultFetchText = async (url: string): Promise<string> => {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.text();
};

type GroupDetection = { state: 'loading' } | { state: 'done'; groups: string[] } | { state: 'error' };

const AddStructureModal: React.FC<AddStructureModalProps> = ({
  streams,
  files,
  structures,
  busy = false,
  onCancel,
  onConfirm,
  fetchText = defaultFetchText,
}) => {
  const timberListTracks = useMemo(() => timberListTracksForStructureSetup(streams), [streams]);
  const groups = useMemo(() => uploadStructureGroups(streams, structures), [streams, structures]);
  const [row, setRow] = useState<StructureFromTimberListRow>(emptyRow);
  const [destKind, setDestKind] = useState<'new' | 'existing'>('new');
  const [groupLabel, setGroupLabel] = useState('');
  const [existingGroupId, setExistingGroupId] = useState(groups[0]?.id ?? '');
  const [detection, setDetection] = useState<GroupDetection | null>(null);

  const versions = useMemo(() => {
    if (!row.timberListStreamId) return [];
    const stream = timberListTracks.find(s => s.id === row.timberListStreamId);
    return stream ? versionsForStream(stream, files) : [];
  }, [row.timberListStreamId, timberListTracks, files]);

  useEffect(() => {
    if (!row.timberListFileId) {
      setDetection(null);
      return undefined;
    }
    const file = files.find(f => f.id === row.timberListFileId);
    if (!file?.url) {
      setDetection({ state: 'error' });
      return undefined;
    }
    let cancelled = false;
    setDetection({ state: 'loading' });
    fetchText(file.url)
      .then(text => {
        if (cancelled) return;
        const found = detectTimberListGroups(text, { trueDimensions: file.trueDimensions === true });
        setDetection({ state: 'done', groups: found });
        setRow(cur =>
          cur.timberListFileId === file.id ? { ...cur, groupNames: found.length > 1 ? found : undefined } : cur,
        );
      })
      .catch(() => {
        if (!cancelled) setDetection({ state: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [row.timberListFileId, files, fetchText]);

  useEffect(() => {
    if (busy) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  const detectedGroups = detection?.state === 'done' && detection.groups.length > 1 ? detection.groups : null;
  const splitting = !!row.splitByGroup && !!detectedGroups;
  const selectedGroup = groups.find(g => g.id === existingGroupId) ?? null;
  const rowProblem = newStructureRowProblem(row, structures);
  const destProblem = destKind === 'existing' && !selectedGroup ? 'Choose a structure group.' : null;
  const problem = rowProblem || destProblem;
  const shownProblem = rowProblem === 'Name the new structure.' ? destProblem : problem;

  const confirm = () => {
    if (problem || busy) return;
    onConfirm({
      row,
      destination:
        destKind === 'new'
          ? { kind: 'new', groupLabel: groupLabel.trim() || undefined }
          : { kind: 'existing', structureIds: selectedGroup!.structureIds },
    });
  };

  const modal = (
    <div
      className="pf-modal-overlay"
      role="presentation"
      onMouseDown={e => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div
        className="pf-modal pf-modal--split pf-modal--structures"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pf-add-structure-title"
        onMouseDown={e => e.stopPropagation()}
      >
        <div className="pf-split-modal-head">
          <h3 className="pf-modal-title" id="pf-add-structure-title">
            <Layers size={16} aria-hidden /> Add a structure
          </h3>
          <button type="button" className="pf-split-modal-close" disabled={busy} aria-label="Close" onClick={onCancel}>
            <X size={16} aria-hidden />
          </button>
        </div>

        <p className="pf-modal-hint">
          This adds a structure to the proposal. A new group starts today: earlier whole-project files stay with the
          groups that already have them, and files uploaded after this are included. Or add it to a group that already
          exists. Structures already in a group stay where they are.
        </p>

        <section className="pf-split-section" aria-labelledby="pf-add-structure-fields">
          <h4 className="pf-split-section-title" id="pf-add-structure-fields">
            Structure
          </h4>
          <div className="pf-split-targets">
            <div className="pf-split-target pf-structures-row">
              <div className="pf-split-target-fields">
                {splitting ? (
                  <div className="pf-split-field pf-split-field--grow">
                    <span className="pf-vr-mu">Structures (one per Building in the CSV)</span>
                    <div className="pf-text-input pf-text-input-height pf-structures-groupnames" title={detectedGroups!.join(', ')}>
                      {detectedGroups!.join(' + ')}
                    </div>
                  </div>
                ) : (
                  <label className="pf-split-field pf-split-field--grow">
                    <span className="pf-vr-mu">Structure name *</span>
                    <input
                      type="text"
                      className="pf-text-input pf-text-input-height"
                      value={row.name}
                      placeholder="e.g. Shed"
                      disabled={busy}
                      onChange={e => setRow(cur => ({ ...cur, name: e.target.value }))}
                    />
                  </label>
                )}
                <label className="pf-split-field pf-split-field--grow">
                  <span className="pf-vr-mu">Timber list (optional)</span>
                  <select
                    className="pf-tag-select pf-text-input-height"
                    value={row.timberListStreamId ?? NONE_VALUE}
                    disabled={busy}
                    onChange={e => {
                      const streamId = e.target.value || null;
                      const stream = timberListTracks.find(s => s.id === streamId);
                      const fileId = stream ? headVersionForStream(stream, files)?.id ?? stream.headFileId ?? null : null;
                      const version =
                        stream && fileId ? versionsForStream(stream, files).find(v => v.id === fileId) : undefined;
                      const suggested = version?.name ? suggestStructureNameFromTrack(version.name) : '';
                      setRow(cur => ({
                        ...cur,
                        timberListStreamId: streamId,
                        timberListFileId: fileId,
                        splitByGroup: false,
                        groupNames: undefined,
                        name: !cur.name.trim() && suggested ? suggested : cur.name,
                      }));
                    }}
                  >
                    <option value={NONE_VALUE}>None — create empty</option>
                    {timberListTracks.map(s =>
                      s.id ? (
                        <option key={s.id} value={s.id}>
                          {trackKindShort(s.streamKind)} · {s.title}
                        </option>
                      ) : null,
                    )}
                  </select>
                </label>
                {row.timberListStreamId && versions.length > 0 ? (
                  <label className="pf-split-field pf-split-field--grow">
                    <span className="pf-vr-mu">Version</span>
                    <select
                      className="pf-tag-select pf-text-input-height"
                      value={row.timberListFileId ?? NONE_VALUE}
                      disabled={busy}
                      onChange={e => {
                        const fileId = e.target.value || null;
                        const version = versions.find(v => v.id === fileId);
                        const suggested = version?.name ? suggestStructureNameFromTrack(version.name) : '';
                        setRow(cur => ({
                          ...cur,
                          timberListFileId: fileId,
                          splitByGroup: false,
                          groupNames: undefined,
                          name: !cur.name.trim() && suggested ? suggested : cur.name,
                        }));
                      }}
                    >
                      {versions.map(v =>
                        v.id ? (
                          <option key={v.id} value={v.id}>
                            {timberListVersionOptionLabel(v)}
                          </option>
                        ) : null,
                      )}
                    </select>
                  </label>
                ) : null}
                {detectedGroups ? (
                  <label className="pf-structures-groups-toggle">
                    <input
                      type="checkbox"
                      checked={!!row.splitByGroup}
                      disabled={busy}
                      onChange={e =>
                        setRow(cur => ({ ...cur, splitByGroup: e.target.checked, groupNames: detectedGroups }))
                      }
                    />
                    <span>
                      This CSV covers <strong>{detectedGroups.length}</strong> Buildings ({detectedGroups.join(', ')}) —
                      create one structure per Building and keep them together
                    </span>
                  </label>
                ) : detection?.state === 'loading' ? (
                  <span className="pf-structures-detect-note">
                    <Loader2 size={11} className="pf-spin" aria-hidden /> Reading the CSV…
                  </span>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <section className="pf-split-section" aria-labelledby="pf-add-structure-dest">
          <h4 className="pf-split-section-title" id="pf-add-structure-dest">
            Where it goes on Files
          </h4>
          <div className="pf-move-choice" role="radiogroup" aria-label="Where the new structure goes">
            <label>
              <input
                type="radio"
                name="pf-add-structure-dest"
                checked={destKind === 'new'}
                disabled={busy}
                onChange={() => setDestKind('new')}
              />
              New group
            </label>
            {destKind === 'new' ? (
              <div className="pf-move-fields">
                <label className="pf-split-field pf-split-field--grow">
                  <span className="pf-vr-mu">Group name (optional)</span>
                  <input
                    type="text"
                    className="pf-text-input pf-text-input-height"
                    value={groupLabel}
                    placeholder="Leave blank to use the structure name"
                    disabled={busy}
                    onChange={e => setGroupLabel(e.target.value)}
                  />
                </label>
              </div>
            ) : null}
            <label>
              <input
                type="radio"
                name="pf-add-structure-dest"
                checked={destKind === 'existing'}
                disabled={busy || groups.length === 0}
                onChange={() => setDestKind('existing')}
              />
              Add to an existing group
              {groups.length === 0 ? <span className="pf-split-track-meta"> — none yet</span> : null}
            </label>
            {destKind === 'existing' ? (
              <div className="pf-move-fields">
                <label className="pf-split-field pf-split-field--grow">
                  <span className="pf-vr-mu">Group</span>
                  <select
                    className="pf-tag-select pf-text-input-height"
                    value={existingGroupId}
                    disabled={busy}
                    onChange={e => setExistingGroupId(e.target.value)}
                  >
                    {groups.map(g => (
                      <option key={g.id} value={g.id}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            ) : null}
          </div>
        </section>

        {shownProblem ? <p className="pf-split-problem">{shownProblem}</p> : null}

        <div className="pf-modal-actions">
          <button type="button" className="pf-modal-cancel" disabled={busy} onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="pf-upload-btn pf-upload-btn--accent" disabled={busy || !!problem} onClick={confirm}>
            {busy ? (
              <>
                <Loader2 size={14} className="pf-spin" /> Adding…
              </>
            ) : (
              <>
                <Layers size={14} aria-hidden /> Add structure
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default AddStructureModal;
